import { useEffect, useRef } from "react";

/**
 * useHeartbeat — fire a heartbeat ping every `intervalMs` (default 60s) while mounted.
 * The first ping fires immediately whenever `enabled` flips to true. Errors are swallowed
 * (best-effort presence). Pass `enabled=false` to suspend the loop without unmounting.
 */
export default function useHeartbeat(pingFn, intervalMs = 60000, enabled = true) {
  const fnRef = useRef(pingFn);

  useEffect(() => {
    fnRef.current = pingFn;
  }, [pingFn]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      try {
        const p = fnRef.current?.();
        if (p && typeof p.catch === "function") {
          p.catch(() => {});
        }
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [intervalMs, enabled]);
}
